import { useEffect, useMemo, useRef, useState } from "react"
import { navigate } from "vike/client/router"
import { motion, AnimatePresence } from "framer-motion"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import {
  InboxArrowDownIcon, PaperAirplaneIcon, CheckIcon, ExclamationTriangleIcon,
  DocumentIcon, XMarkIcon, FolderIcon, ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline"
import { getLinkInfo, beginReceive, finalizeReceive } from "../ReceiveLinks/controller"
import * as TusUploader from "../FileShareFeature/tus-uploader"
import * as ReceiveSession from "./session-store"
import { ThemeShell } from "../FileShareFeature/theme-shell"
import { TopStrip } from "../FileShareFeature/ShareLandingPage"
import FileTree from "../FileShareFeature/FileTree"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { GetCMSContent } from "aldoo-ra/CMS/api"

// Public landing page for a receive-link. The sender:
//   1. lands here, sees the owner's theme + name,
//   2. signs in (magic link) if not already,
//   3. picks files, optionally adds title + notes,
//   4. uploads via the existing TUS endpoint,
//   5. finalizes — server promotes the Order to a completed Share, emits
//      mails (recipient + sender confirmation).
//
// The page reuses the same theme-as-CSS-vars mechanism as ShareLandingPage so
// the recipient's branding (background, primary, text colors) actually shows
// through on Tailwind utilities.

// Match ShareLandingPage: pick a random image from the CMS
// "share_backgrounds" gallery so theme-less branches still have a
// branded backdrop instead of a flat surface.
function useRandomShareBackground() {
  const [bg, setBg] = useState(null)
  useEffect(() => {
    let active = true
    GetCMSContent({ match: { "content.type": "ImageGallery", "content.id": "share_backgrounds" } })
      .then((res) => {
        const images = res?.data?.[0]?.content?.images
        if (!active || !Array.isArray(images) || !images.length) return
        const pick = images[Math.floor(Math.random() * images.length)]
        setBg(cmsUrl(pick))
      })
      .catch(() => {})
    return () => { active = false }
  }, [])
  return bg
}

function formatBytes(bytes) {
  if (!bytes) return "0 B"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export default function ReceivePage() {
  const token = useMemo(() => {
    if (typeof window === "undefined") return ""
    const m = window.location.pathname.match(/^\/r\/([^/?#]+)/)
    return m ? decodeURIComponent(m[1]) : ""
  }, [])
  const [auth] = PlatformAuth.useAuth()

  const [linkInfo, setLinkInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [files, setFiles] = useState([]) // [{ file, path }]
  const [title, setTitle] = useState("")
  const [notes, setNotes] = useState("")
  const [password, setPassword] = useState("")
  const [progress, setProgress] = useState(0)
  const [sentBytes, setSentBytes] = useState(0)
  const [totalBytes, setTotalBytes] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [resumeSession, setResumeSession] = useState(null)
  const [resumeMode, setResumeMode] = useState(false)
  const [missingResumeFiles, setMissingResumeFiles] = useState([])
  const uploadAbortRef = useRef(null)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null) // { share_token, scan_pending }
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Pick up any prior in-flight upload for this exact token. The session
  // remembers the minted orderID + the files we expected so tus-js-client
  // can resume each file from its committed offset when the user re-picks.
  useEffect(() => {
    if (!token) return
    const sess = ReceiveSession.load(token)
    if (sess) {
      setResumeSession(sess)
      setMissingResumeFiles(sess.files || [])
      setTitle(sess.title || "")
      setNotes(sess.notes || "")
    }
  }, [token])

  // Fetch the link's public info on mount.
  useEffect(() => {
    if (!token) return
    let cancel = false
    ;(async () => {
      try {
        const res = await getLinkInfo(token)
        if (cancel) return
        if (res?.error) setError(res.error)
        else setLinkInfo(res?.link || null)
      } finally {
        if (!cancel) setLoading(false)
      }
    })()
    return () => { cancel = true }
  }, [token])

  // Logged-in = good enough. The profile email is the identity attached to
  // the Order's meta.sender_user / sender_email and feeds the Sent tab.
  const verifiedUser = !!auth
  const texts = TextBundle({ id: "transfer_receive_page" })
  const { translateWithLanguage, getLanguage } = Localization()
  const t = (key, data) => translateWithLanguage(key, getLanguage(), data)

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || [])
    let next = arr.map((f) => ({ file: f, path: f.webkitRelativePath || f.name }))
    // Resume mode — rewrite the picked file paths to the originally-
    // recorded relative paths so tus's fingerprint matches the prior
    // upload session and the file can pick up from its committed offset.
    if (resumeSession) {
      const { matched, missing, extra } = ReceiveSession.matchPickedFiles(next, resumeSession.files || [])
      next = [...matched, ...extra]
      setMissingResumeFiles(missing)
    }
    const link = linkInfo
    // Client-side caps so the user gets feedback before the upload starts.
    if (link?.max_files_per_upload && (files.length + next.length) > link.max_files_per_upload) {
      setError(t(texts?.error_too_many_files, { max: link.max_files_per_upload }))
      return
    }
    if (link?.allowed_extensions && link.allowed_extensions.length) {
      const bad = next.find((e) => !link.allowed_extensions.includes(
        (e.file.name.split(".").pop() || "").toLowerCase()
      ))
      if (bad) {
        setError(t(texts?.error_file_type_not_allowed, { name: bad.file.name }))
        return
      }
    }
    setError(null)
    setFiles((prev) => [...prev, ...next])
  }

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))

  const aggregateSize = files.reduce((s, e) => s + e.file.size, 0)

  const handleCancel = async () => {
    const ok = await showPromptDialogAsync({
      title: texts?.cancel_dialog_title,
      message: texts?.cancel_dialog_message,
      yesLabel: texts?.cancel_dialog_yes,
      noLabel: texts?.cancel_dialog_no,
    })
    if (!ok) return
    uploadAbortRef.current?.abort()
    uploadAbortRef.current = null
    setUploading(false)
    setProgress(0)
    setSentBytes(0)
    setTotalBytes(0)
  }

  const handleSend = async () => {
    if (uploading || !files.length) return
    if (!verifiedUser) {
      PubSub.publish("signin", {})
      return
    }
    if (linkInfo?.max_aggregate_size && aggregateSize > linkInfo.max_aggregate_size) {
      setError(t(texts?.error_files_exceed_limit, { limit: formatBytes(linkInfo.max_aggregate_size) }))
      return
    }
    if (linkInfo?.password_required && !resumeSession?.orderID && !password) {
      setError(texts?.error_password_required)
      return
    }
    setError(null)
    setUploading(true)
    setProgress(0)
    setSentBytes(0)
    setTotalBytes(aggregateSize)
    try {
      // Reuse the previously-minted orderID if we're resuming — that's
      // how tus-js-client can keep its per-file fingerprint URLs valid.
      let orderID = resumeSession?.orderID
      if (!orderID) {
        const begin = await beginReceive(token, password)
        if (begin?.error || !begin?.orderID) {
          setError(
            begin?.error === "owner_over_quota"
              ? t(texts?.error_owner_over_quota, { owner: linkInfo.owner_name })
              : begin?.error === "invalid_password"
              ? texts?.error_invalid_password
              : begin?.error === "access_denied"
              ? t(texts?.error_access_denied, { owner: linkInfo.owner_name })
              : (begin?.error || "begin_failed"),
          )
          setUploading(false)
          return
        }
        orderID = begin.orderID
      }
      ReceiveSession.save(token, { orderID, entries: files, title, notes })
      const abortController = new AbortController()
      uploadAbortRef.current = abortController
      await TusUploader.uploadMany({
        orderID,
        entries: files,
        signal: abortController.signal,
        onProgress: (sent, total) => {
          setSentBytes(sent)
          setTotalBytes(total)
          setProgress(total ? sent / total : 0)
        },
      })
      const finalizeRes = await finalizeReceive({ orderID, title, notes })
      if (finalizeRes?.error) {
        setError(
          finalizeRes.error === "owner_over_quota"
            ? t(texts?.error_storage_full, { owner: linkInfo.owner_name })
            : finalizeRes.error,
        )
        setUploading(false)
        return
      }
      ReceiveSession.clear(token)
      setResumeSession(null)
      setDone(finalizeRes)
    } catch (e) {
      setError(e?.message || "upload_failed")
    } finally {
      setUploading(false)
    }
  }

  const theme = linkInfo?.theme || null
  const randomBg = useRandomShareBackground()
  const fallbackBg = !theme ? randomBg : null

  if (loading) {
    return (
      <ThemeShell theme={theme} bg={fallbackBg}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </ThemeShell>
    )
  }

  if (!linkInfo) {
    return (
      <ThemeShell theme={null} bg={fallbackBg} fallbackClass="bg-indigo-950">
        <TopStrip hideGuestCTA={false} />
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12 lg:items-start lg:justify-center lg:px-20">
          <div className="bg-paper/85 backdrop-blur-[80px] border border-bordered shadow-[0_0_20px_0_rgba(0,0,0,0.25)] rounded-2xl max-w-md w-full p-8 text-center flex flex-col items-center gap-3">
            <ExclamationTriangleIcon className="w-12 h-12 text-text_secondary" />
            <Typography variant="h3">
              {texts?.link_not_found_heading}
            </Typography>

            <Typography variant="txt_dim">
                {texts?.link_not_found_body}
            </Typography>
          </div>
        </div>
      </ThemeShell>
    )
  }

  // Owner viewing their own receive link — send them to the dashboard
  // page where they manage it. They can't upload to themselves.
  const isOwner = auth && linkInfo.owner_id && String(auth._id) === String(linkInfo.owner_id)
  if (isOwner) {
  return (
    <ThemeShell theme={theme} bg={fallbackBg}>
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12 lg:items-start lg:justify-center lg:px-20">
        <div className="bg-paper/85 backdrop-blur-[80px] border border-bordered shadow-[0_0_20px_0_rgba(0,0,0,0.25)] rounded-2xl max-w-md w-full p-8 text-center">
          <Typography variant="h3" className="mb-2">
            {texts?.owner_view_heading}
          </Typography>

          <Typography variant="txt_dim" className="block mb-5">
              {texts?.owner_view_body}
          </Typography>

          <Button
            variant="primary"
            onClick={() => navigate("/account/receive-links")}
            className="w-full !h-[52px]"
          >
            {texts?.owner_view_cta}
          </Button>
        </div>
      </div>
    </ThemeShell>
    )
  }

  if (!linkInfo.link_active) {
    const reason = linkInfo.inactive_reason
    return (
      <ThemeShell theme={theme} bg={fallbackBg}>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center gap-3 p-6">
          <ExclamationTriangleIcon className="w-12 h-12 text-warning" />
          <Typography variant="h3">
            {reason === "paused" ? texts?.inactive_paused_heading
              : reason === "expired" ? texts?.inactive_expired_heading
              : reason === "full" ? texts?.inactive_full_heading
              : texts?.inactive_unknown_heading}
          </Typography>
          <Typography variant="txt_dim">
            {reason === "paused" ? texts?.inactive_paused_body
              : reason === "expired" ? texts?.inactive_expired_body
              : reason === "full" ? texts?.inactive_full_body
              : texts?.inactive_unknown_body}
          </Typography>
        </div>
      </ThemeShell>
    )
  }

  if (done) {
    const sendMore = () => {
      setDone(null)
      setFiles([])
      setTitle("")
      setNotes("")
      setError(null)
      setProgress(0)
      setSentBytes(0)
      setTotalBytes(0)
      setResumeSession(null)
      setMissingResumeFiles([])
      ReceiveSession.clear(token)
    }
    return (
      <ThemeShell theme={theme} bg={fallbackBg}>
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12 lg:items-start lg:justify-center lg:px-20">
          <div className="bg-paper/85 backdrop-blur-[80px] border border-bordered shadow-[0_0_20px_0_rgba(0,0,0,0.25)] rounded-2xl max-w-md w-full p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto mb-4">
              <CheckIcon className="w-8 h-8" />
            </div>
            <Typography variant="h3" className="mb-2">{texts?.done_heading}</Typography>
            <Typography variant="txt_dim" className="block mb-4">
              {t(texts?.done_notified_fmt, { owner: linkInfo.owner_name })}
              {done.scan_pending && (" " + texts?.done_scan_pending)}
            </Typography>
            <Button
              variant="primary"
              onClick={sendMore}
              className="w-full !h-[52px]"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
              {texts?.done_send_more_btn}
            </Button>
          </div>
        </div>
      </ThemeShell>
    )
  }

  return (
    <ThemeShell theme={theme} bg={fallbackBg}>
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12 lg:items-start lg:justify-center lg:px-20">
      <div className="relative bg-paper/85 backdrop-blur-[80px] border border-bordered shadow-[0_0_20px_0_rgba(0,0,0,0.25)] rounded-2xl max-w-md w-full max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col">
        <div className="overflow-y-auto p-6 md:p-8">
        {/* Owner header — avatar + name (+ email when distinct).
          Falls back to initial + email when no avatar. */}
        {(() => {
          const avatarSrc = linkInfo.owner_avatar ? (() => { try { return cmsUrl(linkInfo.owner_avatar) } catch { return null } })() : null
          const initial = (linkInfo.owner_name || "?")[0].toUpperCase()
          return (
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-bordered">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-12 h-12 rounded-full object-cover border border-bordered" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-lg font-semibold">
                  {initial}
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-text truncate">
                  {linkInfo.owner_name || linkInfo.owner_email}
                </span>
                {linkInfo.owner_has_name && linkInfo.owner_email && (
                  <span className="text-xs text-text_secondary truncate">{linkInfo.owner_email}</span>
                )}
              </div>
            </div>
          )
        })()}

        {(linkInfo.name || linkInfo.sender_message) && (
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            title={linkInfo.sender_message ? texts?.details_btn_read_message : texts?.details_btn_details}
            className="absolute top-3 right-3 z-[5] w-9 h-9 rounded-full border border-bordered bg-canvas text-text flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <ChatBubbleLeftEllipsisIcon className="w-4 h-4" strokeWidth={2} />
          </button>
        )}

        {!verifiedUser ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <Typography variant="txt_dim" className="block">
              {t(texts?.verify_prompt_fmt, { owner: linkInfo.owner_name })}
            </Typography>
            <Button
              variant="primary"
              onClick={() => PubSub.publish("signin", {})}
              className="w-full !h-[52px]"
            >
              {texts?.verify_btn}
            </Button>
          </div>
        ) : (
        <>
        {/* Password gate — required by the link owner. Enforced
          server-side at begin; this just collects it. */}
        {linkInfo.password_required && !resumeSession?.orderID && (
          <div className="mb-4">
            <Typography variant="txt2_dim" className="block mb-2">
              {texts?.password_label}
            </Typography>
            <Input
              variant="transfer_compact"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onEnter={handleSend}
              placeholder={texts?.password_placeholder}
              disabled={uploading}
              className="w-full"
            />
          </div>
        )}

        {/* Resume banner — when we have a saved session for this
          token, prompt the user to re-pick files so tus can
          continue from each file's committed offset. */}
        {resumeSession && !uploading && !done && (
          <div className="bg-progress/10 border border-progress/30 rounded-xl px-4 py-3 mb-4 text-sm text-text">
            <div className="font-medium mb-1">{texts?.resume_heading}</div>
            <div className="text-xs text-text_secondary mb-2">
              {missingResumeFiles.length > 0
                ? t(texts?.resume_repick_fmt, {
                    count: missingResumeFiles.length,
                    word: missingResumeFiles.length === 1 ? texts?.file_singular : texts?.file_plural,
                  })
                : texts?.resume_all_matched}
            </div>
            {missingResumeFiles.length > 0 && (
              <ul className="text-xs text-text_secondary list-disc pl-4 max-h-24 overflow-y-auto">
                {missingResumeFiles.slice(0, 5).map((f) => (
                  <li key={f.path} className="truncate">{f.path}</li>
                ))}
                {missingResumeFiles.length > 5 && <li>{t(texts?.resume_more_fmt, { count: missingResumeFiles.length - 5 })}</li>}
              </ul>
            )}
            <button
              type="button"
              className="text-xs text-error hover:underline mt-2"
              onClick={() => {
                ReceiveSession.clear(token)
                setResumeSession(null)
                setMissingResumeFiles([])
                setFiles([])
              }}
            >
              {texts?.resume_discard_btn}
            </button>
          </div>
        )}

        {/* File picker */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              disabled={uploading}
            />
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-bordered text-sm text-text hover:border-primary">
              <DocumentIcon className="w-4 h-4" /> {texts?.add_files_btn}
            </span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              disabled={uploading}
            />
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-bordered text-sm text-text hover:border-primary">
              <FolderIcon className="w-4 h-4" /> {texts?.add_folder_btn}
            </span>
          </label>
        </div>

        {/* File list — same FileTree the create-share flow uses, so
          folder structures and per-file remove work identically. */}
        {files.length > 0 && (
          <div className="mb-4">
            <FileTree
              entries={files.map((e, i) => ({ path: e.path, file: e.file, size: e.file.size, index: i }))}
              onRemove={uploading ? undefined : (path) => {
                setFiles((prev) => prev.filter((e) => e.path !== path && !e.path.startsWith(path + "/")))
              }}
              disabled={uploading}
              maxHeightClass="max-h-72"
            />
            <div className="px-1 py-2 text-xs text-text_secondary text-right">
              {files.length} {files.length === 1 ? texts?.file_singular : texts?.file_plural} · {formatBytes(aggregateSize)}
            </div>
          </div>
        )}

        {/* More options — Title + Message, collapsed by default so
          the file tree gets the vertical real estate. */}
        <div className="mb-4 flex flex-col items-center">
          <button
            type="button"
            onClick={() => setMoreOptionsOpen((v) => !v)}
            className="text-xs font-medium text-text_secondary hover:text-text inline-flex items-center gap-1"
          >
            <span>{moreOptionsOpen ? texts?.hide_options_btn : texts?.more_options_btn}</span>
            <span className={`inline-block transition-transform ${moreOptionsOpen ? "rotate-180" : ""}`}>▾</span>
          </button>
          {moreOptionsOpen && (
            <div className="grid gap-3 mt-3 w-full">
              <div>
                <Typography variant="txt2_dim" className="block mb-1">{texts?.title_label}</Typography>
                <Input
                  variant="transfer_compact"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onEnter={handleSend}
                  placeholder={texts?.title_placeholder}
                  className="w-full"
                  disabled={uploading}
                />
              </div>
              <div>
                <Typography variant="txt2_dim" className="block mb-1">{texts?.message_label}</Typography>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={texts?.message_placeholder}
                  className="w-full rounded-xl border border-bordered bg-paper px-3 py-2.5 text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                  disabled={uploading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {uploading && (
          <div className="mb-4">
            <div className="h-2 rounded-full bg-neutral_1 overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-150"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <Typography variant="txt2_dim" className="block text-right mt-1">
              {formatBytes(sentBytes)} / {formatBytes(totalBytes)} · {Math.round(progress * 100)}%
            </Typography>
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error/30 rounded-xl px-4 py-3 mb-4">
            <Typography variant="txt2_dim" className="text-error">{error}</Typography>
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleSend}
          disabled={uploading || !verifiedUser || !files.length}
          className="w-full !h-[52px]"
        >
          {uploading ? (
            <Spinner variant="minimal" />
          ) : (
            <>
              <PaperAirplaneIcon className="w-5 h-5" />
              {texts?.send_now_btn}
            </>
          )}
        </Button>
        {uploading && (
          <Button
            variant="secondary_red_s"
            onClick={handleCancel}
            className="w-full mt-2"
          >
            {texts?.cancel_btn}
          </Button>
        )}
        </>
        )}
        </div>

        {/* Details overlay — scoped to the upload card. Click
          anywhere (or the closer pill) to dismiss. */}
        <AnimatePresence>
          {detailsOpen && (
            <motion.div
              className="absolute inset-0 z-[10] bg-paper/95 backdrop-blur-md flex flex-col items-center justify-center p-6 cursor-pointer overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setDetailsOpen(false)}
            >
              <motion.div
                className="w-full text-center flex flex-col items-center gap-5"
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 8, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {(() => {
                  const avatarSrc = linkInfo.owner_avatar ? (() => { try { return cmsUrl(linkInfo.owner_avatar) } catch { return null } })() : null
                  const initial = (linkInfo.owner_name || "?")[0].toUpperCase()
                  return avatarSrc ? (
                    <img src={avatarSrc} alt="" className="w-16 h-16 rounded-full object-cover border border-bordered" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-semibold">
                      {initial}
                    </div>
                  )
                })()}
                {linkInfo.name && (
                  <h2 className="text-2xl md:text-3xl font-bold text-text leading-tight break-words">
                    {linkInfo.name}
                  </h2>
                )}
                {linkInfo.sender_message && (
                  <p className="text-sm md:text-base text-text_secondary leading-relaxed whitespace-pre-wrap break-words">
                    {linkInfo.sender_message}
                  </p>
                )}
              </motion.div>
              <span className="absolute bottom-4 left-0 right-0 text-center text-xs text-text_secondary pointer-events-none">
                {texts?.overlay_close_hint}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </ThemeShell>
  )
}
